import { Link, useParams } from 'react-router-dom';
import { Plus, Store, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ProductList } from '@/components/products';
import { useProducerProducts, ProducerProduct } from '@/hooks/useProducerProducts';
import { useProducerById } from '@/hooks/useMyProducers';
import { useToast } from '@/hooks/use-toast';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { useProducerAccess } from '@/hooks/useProducerAccess';
import { useProducerUsage } from '@/hooks/useProducerUsage';
import { UsageBadge } from '@/components/producer/UsageBadge';
import { SoftLimitMessage } from '@/components/producer/SoftLimitMessage';

export default function ProducerProductList() {
  const { farmId } = useParams<{ farmId: string }>();
  const { producer, isLoading: producerLoading } = useProducerById(farmId);
  const { products, loading, error, deleteProduct, updateProduct, reorderProducts } = useProducerProducts(farmId);
  const { toast } = useToast();
  const { hasCapability, isReadOnly } = useProducerAccess(farmId);
  const { data: usage } = useProducerUsage(farmId);
  
  const canManageShop = hasCapability('canManageShop');
  const limitReached = usage != null && usage.products_limit !== null && usage.products_used >= usage.products_limit;

  const basePath = `/app/producer/farm/${farmId}/shop`;

  const handleDeleteProduct = async (product: ProducerProduct) => {
    try {
      await deleteProduct(product.id);
      toast({
        title: 'Produkt borttagen',
        description: `${product.title} har tagits bort.`,
      });
    } catch (err) {
      toast({
        title: 'Kunde inte ta bort',
        description: 'Något gick fel. Försök igen.',
        variant: 'destructive',
      });
    }
  };

  const handleToggleStatus = async (product: ProducerProduct) => {
    const newStatus = product.status === 'public' ? 'draft' : 'public';
    const description = newStatus === 'public'
      ? 'Produkten är nu synlig i din gårdsbutik.'
      : 'Produkten är inte längre synlig i din gårdsbutik.';
    try {
      await updateProduct({ id: product.id, status: newStatus });
      toast({ title: newStatus === 'public' ? 'Publicerad' : 'Avpublicerad', description });
    } catch (err) {
      toast({
        title: 'Kunde inte ändra status',
        description: 'Något gick fel. Försök igen.',
        variant: 'destructive',
      });
    }
  };

  const handleReorder = async (orderedIds: string[]) => {
    try {
      await reorderProducts(orderedIds);
      toast({ title: 'Ordningen sparad' });
    } catch {
      toast({
        title: 'Kunde inte spara ordning',
        description: 'Något gick fel. Försök igen.',
        variant: 'destructive',
      });
    }
  };

  if (producerLoading) {
    return (
      <ProducerLayout 
        farmId={farmId || ''} 
        producerName="Laddar..." 
        title="Produkter"
      >
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout 
      farmId={farmId || ''} 
      producerName={producer?.name}
      title="Produkter"
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl md:text-2xl font-semibold text-foreground">Produkter</h1>
                {usage && (
                  <UsageBadge used={usage.products_used} limit={usage.products_limit} label="Produkter" />
                )}
              </div>
              <p className="text-muted-foreground text-sm mt-1">
                Hantera produkter i din gårdsbutik
              </p>
            </div>
            {isReadOnly && (
              <Badge variant="outline" className="text-muted-foreground gap-1">
                Endast läsning
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            {producer?.slug && (
              <Button variant="outline" size="sm" asChild className="hidden md:inline-flex">
                <Link to={`/producer/${producer.slug}/shop`}>
                  <Store className="h-4 w-4 mr-2" />
                  Visa butik
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            )}
            {canManageShop && (
              <Button asChild={!limitReached} disabled={limitReached} size="sm">
                {limitReached ? (
                  <span>
                    <Plus className="h-4 w-4 mr-2" />
                    Skapa produkt
                  </span>
                ) : (
                  <Link to={`${basePath}/products/new`}>
                    <Plus className="h-4 w-4 mr-2" />
                    Skapa produkt
                  </Link>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Soft limit message */}
        {usage && (
          <SoftLimitMessage used={usage.products_used} limit={usage.products_limit} resourceName="produkter" />
        )}

        {/* Product list - Shopify is disabled */}
        <ProductList
          products={products}
          loading={loading}
          error={error}
          onDeleteProduct={handleDeleteProduct}
          onToggleStatus={canManageShop ? handleToggleStatus : undefined}
          onReorder={canManageShop ? handleReorder : undefined}
          basePath={basePath}
        />
      </div>
    </ProducerLayout>
  );
}
